import React, { useEffect, useState } from 'react'
import shipicon from '../assets/images/ship-icon.svg'
import flighticon from '../assets/images/flight-icon.svg'
import activeshipicon from '../assets/images/active-ship-icon.svg'
import activeflighticon from '../assets/images/active-flight-icon.svg'
import { useDispatch, useSelector } from 'react-redux'
import { clearstate, getSingleBookingData, searchratestate, setOceanEditQuery, setOceanEditLock } from '../store/SearchRates/SearchRatesSlice'
import { get_single_search_query } from '../store/Admin/Queries/QuerySlice'
import { notification, Spin } from 'antd'
import { kycdocstatus } from '../store/User/KycSlice'
import { useLocation, useNavigationType } from 'react-router-dom'
import ShipmentRates from '../components/SearchRates/ShipmentRates'

import { authuser, singleSignonService } from '../store/Authentication/AuthSlice'
import NewsearchForm from '../forms/SearchRates/FlightRate/Newsearchform/NewsearchForm'
import { mapBookingSearchQuery } from '../utils/openQueryResults'

// `mapBookingSearchQuery` now lives in utils/openQueryResults.js — it is shared
// with the results pages' `?queryId=` deep-link bootstrap (the quotation email
// links straight there), and keeping it in a dependency-free module stops that
// import from pulling this whole page into their bundles. Re-exported here so
// every existing `import { mapBookingSearchQuery } from '../NewSearchRates'`
// keeps working unchanged.
export { mapBookingSearchQuery } from '../utils/openQueryResults'

const NewSearchRates = () => {
    const location = useLocation()
    // Deep-link into the ocean form with a saved query: `/search-rates?_id=<queryId>`
    // (sent from the Booking History edit action). When present we fetch that
    // query and auto-populate the form from its `searchquery`.
    const bookingQueryId = new URLSearchParams(location?.search).get('_id')
    // The Queries-page "Edit" action deep-links here as
    // `/search-rates?queryId=<id>&pagetype=QUERIES`. For AIR the air form
    // (NewsearchForm) reads those params and prefills itself; for OCEAN the
    // ShipmentRateForm only reads its `searchFormData` prop, so THIS page must
    // fetch the saved query and push it into redux (mirroring the `?_id=` path
    // below). Detect the ocean case from the record handed over in router state.
    const queriesEditId = new URLSearchParams(location?.search).get('queryId')
    const isOceanQueriesEdit =
        new URLSearchParams(location?.search).get('pagetype') === 'QUERIES' &&
        !!queriesEditId &&
        location?.state?.cargoType === 'Ocean Cargo'
    // Honour a tab hint passed via router state (e.g. the ocean results page's
    // "Edit Query" button sends { searchBy: 'Ocean Cargo' }) so returning here
    // lands on the correct tab instead of the default 'Air Cargo'.
    //
    // ...but ONLY on a push. That hint belongs to the navigation that created
    // the history entry, and it goes stale the moment the user switches mode
    // tabs on the page (a tab switch is local state, it doesn't rewrite the
    // entry). On a POP — browser Back, or the AIR results page's own back
    // button, which is a `navigate(-1)` — React Router replays the entry's
    // original state, so backing out of an air search into an entry an earlier
    // ocean "Edit Query" had pushed reopened this page on the Ocean tab. On a
    // pop the tab is resolved from the redux prefill / the Air default instead
    // (see the tab effect below).
    const navigationType = useNavigationType()
    const [searchBy, setSearchBy] = useState(
        navigationType === 'POP' ? undefined : location?.state?.searchBy
    )
    // While a `?_id=` / ocean-Queries-edit query is being fetched, hold off
    // rendering the form so it mounts ONCE with the prefill already in redux
    // (the form seeds its origin/destination local state from props on mount
    // only — a late prop update would reinitialise Formik but not those states).
    const [bookingPrefillLoading, setBookingPrefillLoading] = useState(!!bookingQueryId || isOceanQueriesEdit)
    const [, contextHolder] = notification.useNotification();
    const dispatch = useDispatch();
    const allSarchState = useSelector(searchratestate);
    // While re-quoting a confirmed booking, the mode tabs (Air/Ocean/Courier) are
    // locked so the user can't switch mode mid-update; only the "Search Rates"
    // nav resets it (see Header). Driven by the redux flag set in the fetch above.
    const editLocked = !!allSarchState?.oceanEditLocked;
    // Edit-from-Queries deep-link (customer/customer-admin "Edit" opens this form
    // with ?pagetype=QUERIES): lock the mode selector to the query's own cargo
    // type so the user can't switch Air⇄Ocean⇄Courier mid-edit — the still-quote
    // -stage sibling of editLocked (which locks a confirmed-booking re-quote).
    // Only the two non-active modes (+ Courier) are disabled; the query's own
    // mode stays selectable/highlighted.
    const isQueriesEdit =
        new URLSearchParams(location?.search).get('pagetype') === 'QUERIES' &&
        !!queriesEditId;
    const airLocked = editLocked || (isQueriesEdit && searchBy !== 'Air Cargo');
    const oceanLocked = editLocked || (isQueriesEdit && searchBy !== 'Ocean Cargo');
    const courierLocked = editLocked || isQueriesEdit;
    const userroles = useSelector(authuser)?.userroles;
    // The old "complete your KYC" toast (linking to /user/kyc) is gone: the
    // business verification ribbon in PageLayout tells every customer what to
    // do, by KYB status. Still refresh that status on entry.
    useEffect(() => {
        dispatch(kycdocstatus())
    }, [dispatch])
    useEffect(() => {
        if (allSarchState?.data?.data?.nodeData) {
            setSearchBy(allSarchState?.data?.nodeData?.searchquery?.cargoType)
        } else if (!searchBy && !bookingQueryId && !isOceanQueriesEdit) {
            setSearchBy('Air Cargo')
        }
    }, [allSarchState?.data, searchBy, bookingQueryId, isOceanQueriesEdit])
    // Fetch the saved query for `?_id=` and push its `searchquery` into redux in
    // the shape ShipmentRateForm reads (data.data.nodeData.searchquery), then
    // reveal the Ocean form. The form remounts fresh here so its local state is
    // seeded from the prefill.
    useEffect(() => {
        // No deep-link (fresh /search-rates) → nothing to prefill and nothing to
        // lock. This also fires when "Search Rates" is clicked to reset (the _id
        // is dropped from the URL), re-enabling the nav + mode tabs.
        if (!bookingQueryId) {
            dispatch(setOceanEditLock(false))
            return
        }
        setBookingPrefillLoading(true)
        // Coming BACK from the ocean results page (browser Back, or its "Edit
        // Query" button which stamps `fromOceanResults`), the redux prefill
        // already holds the containers the user just searched with — incl.
        // their KG/UNIT | TOTAL weightType. Capture it before the fetch so the
        // booking snapshot below doesn't overwrite that selection. Same
        // queryId only, so a different booking never inherits it; a fresh
        // open from Booking History (PUSH, no flag) still uses the snapshot.
        const comingBackFromResults =
            navigationType === 'POP' || !!location?.state?.fromOceanResults
        const pendingSearch = allSarchState?.data?.data?.nodeData?.searchquery
        const pendingContainers =
            comingBackFromResults &&
            String(pendingSearch?.queryId || '') === String(bookingQueryId) &&
            Array.isArray(pendingSearch?.containerDetails) &&
            pendingSearch.containerDetails.length > 0
                ? pendingSearch.containerDetails
                : null
        dispatch(getSingleBookingData(bookingQueryId)).then((res) => {
            // all-ocean-rates should re-quote (requestType 2) ONLY when this
            // saved query actually came back from single_booking_data with a
            // 200; any other outcome must stay a fresh requestType-1 search.
            const fromSingleBooking = res?.payload?.status === 200
            const bookingData = res?.payload?.data?.data
            const searchquery = mapBookingSearchQuery(bookingData?.queryData || bookingData?.searchquery)
            if (searchquery) {
                // Remember the source queryId so a re-search (searchOceanRate)
                // can send queryId + requestType:2 — i.e. re-quote this saved
                // query rather than create a brand-new one.
                searchquery.queryId = bookingQueryId
                searchquery.fromSingleBooking = fromSingleBooking
                // The base mapping comes from queryData, but a few fields are
                // only right on the booking's search_query_data SNAPSHOT — the
                // search this booking was actually made with. Overlay those.
                const snapshot = bookingData?.search_query_data
                // incoTerms/paymentType come back empty on the queryData record.
                const snapshotTerms = snapshot?.paymentTerms
                if (snapshotTerms) {
                    searchquery.paymentTerms.incoTerms =
                        searchquery.paymentTerms.incoTerms || snapshotTerms.incoTerms || ''
                    searchquery.paymentTerms.paymentType =
                        searchquery.paymentTerms.paymentType || snapshotTerms.paymentType || ''
                }
                // Containers: always the snapshot's, since queryData still holds
                // the ORIGINAL query's container mix while the snapshot holds the
                // one this booking was placed with (e.g. re-quoted 2 x 20GP).
                // Accepts both the nested (containerDetails.containerDetails) and
                // flat shapes; an older booking with no snapshot containers keeps
                // whatever queryData gave us.
                const snapshotContainers = Array.isArray(snapshot?.containerDetails)
                    ? snapshot.containerDetails
                    : Array.isArray(snapshot?.containerDetails?.containerDetails)
                    ? snapshot.containerDetails.containerDetails
                    : []
                if (pendingContainers) {
                    searchquery.containerDetails = pendingContainers
                } else if (snapshotContainers.length > 0) {
                    searchquery.containerDetails = snapshotContainers
                }
                // Local Charges toggles: same story as paymentTerms above —
                // queryData is the original query and may predate this field
                // entirely, so prefer the snapshot of what was ACTUALLY
                // searched/booked when it explicitly set one. Coerced rather
                // than `!!` directly since a boolean can round-trip through
                // the API as the string "false" (which is truthy).
                const asBool = (v) => v === false || v === 'false' || v === 0 || v === '0' ? false : !!v
                if (snapshot?.originLocalCharges != null) {
                    searchquery.originLocalCharges = asBool(snapshot.originLocalCharges)
                }
                if (snapshot?.destinationLocalCharges != null) {
                    searchquery.destinationLocalCharges = asBool(snapshot.destinationLocalCharges)
                }
                // booking_status lives at the TOP LEVEL of the single_booking_data
                // record (sibling of queryData/search_query_data — see
                // OceanCargoDetails `nodeData?.booking_status`), NOT inside the
                // mapped query. Propagate it so ShipmentRateForm's
                // `editQueryAlreadyBooked` check sees "Booking Confirmed" and
                // bumps requestType to 2 (update-ocean-booking) instead of 1.
                searchquery.booking_status = bookingData?.booking_status
                const nodeData = { searchquery }
                dispatch(setOceanEditQuery({ data: { nodeData }, nodeData }))
                setSearchBy('Ocean Cargo')
                // Lock the nav + mode tabs ONLY for a genuinely confirmed booking
                // re-quote (user's rule: "when booking confirmed and url ?_id=").
                dispatch(setOceanEditLock(fromSingleBooking && bookingData?.booking_status === 'Booking Confirmed'))
            } else {
                // Couldn't load the saved query — fall back to a blank Ocean form.
                setSearchBy((prev) => prev || 'Ocean Cargo')
                dispatch(setOceanEditLock(false))
            }
            setBookingPrefillLoading(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingQueryId])
    // Ocean Queries-page edit (`?queryId=&pagetype=QUERIES` for an Ocean Cargo
    // query): fetch the saved query via search_cargo_result_detail — the same
    // endpoint the KAM ocean edit uses — and push it into redux in the shape the
    // ocean form reads, then reveal the Ocean tab. Unlike the `?_id=` booking
    // re-quote above, this is a still-quote-stage edit (Auto Quoted / Quote
    // Pending): fields stay EDITABLE (no oceanEditLock, no `fromSingleBooking`)
    // and the re-search goes out as requestType 1 (see ShipmentRateForm onSubmit).
    useEffect(() => {
        if (!isOceanQueriesEdit) return
        setBookingPrefillLoading(true)
        dispatch(get_single_search_query(queriesEditId))
            .then((res) => {
                const full = res?.payload?.data?.nodeData
                // Fall back to the summary record from router state so the form is
                // never left blank if the fetch returns nothing.
                const searchquery = mapBookingSearchQuery(full || location?.state)
                if (searchquery) {
                    // Stamp the queryId so a re-search re-quotes THIS query. We do
                    // NOT set `fromSingleBooking`, so onSubmit keeps requestType 1
                    // and the origin/destination fields remain editable.
                    searchquery.queryId = queriesEditId
                    // Carry the status so ShipmentRateForm's already-booked check
                    // stays correct (Auto Quoted / Quote Pending → requestType 1).
                    searchquery.Quote_status = full?.Quote_status || location?.state?.Quote_status
                    const nodeData = { searchquery }
                    dispatch(setOceanEditQuery({ data: { nodeData }, nodeData }))
                }
                setSearchBy('Ocean Cargo')
                dispatch(setOceanEditLock(false))
                setBookingPrefillLoading(false)
            })
            .catch(() => {
                // Fetch blew up — still open a (blank/partial) Ocean form rather
                // than stranding the user on a spinner.
                setSearchBy('Ocean Cargo')
                dispatch(setOceanEditLock(false))
                setBookingPrefillLoading(false)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queriesEditId, isOceanQueriesEdit])
    function handleSignOn() {
        dispatch(singleSignonService())
            .then(res => {
                window.open(`${process.env.REACT_APP_API_SKART_BACKEND}/api/v1/auth/sso-login?token=${res?.payload?.data?.token}`, "_blank");
            })
            .catch(err => {
                console.error("Error fetching cancel history:", err);
            });
    }
    return (
        <>
            {
                <Spin delay={1000} spinning={allSarchState.loading || bookingPrefillLoading} tip="Searching..." size="large" className='max-h-full text-primary text-lg font-bold text-center'>
                    <div className="flex gap-5 w-full flex-col">
                        <div className="w-full rounded-2xl shadow-cardsection bg-white">
                            {/* ── Header ─────────────────────────────────────────────── */}
                            <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, borderBottom: '1px solid #f0e8d5' }}>

                                {/* Left: title block */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{
                                        width: 32, height: 32, borderRadius: 8,
                                        background: '#ffaf00',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 2px 10px rgba(255,175,0,.35)',
                                        flexShrink: 0,
                                    }}>
                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                                            <line x1="12" y1="22.08" x2="12" y2="12" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 17, fontWeight: 700, color: '#292524', letterSpacing: '.01em', lineHeight: 1 }}>Book Your Cargo</div>
                                        <div style={{ fontSize: 11.5, color: '#a8a29e', marginTop: 3, fontWeight: 500 }}>Select a mode and search for rates</div>
                                    </div>
                                </div>

                                {/* Right: mode selector tabs */}
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                    {/* Air Cargo */}
                                    <button
                                        type="button"
                                        disabled={airLocked}
                                        onClick={() => { setSearchBy('Air Cargo'); if (Object.keys(allSarchState?.data).length > 0) { dispatch(clearstate()) } }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            height: 36, padding: '0 15px',
                                            borderRadius: 10,
                                            border: `2px solid ${searchBy === 'Air Cargo' ? '#ffaf00' : '#e7e0d6'}`,
                                            background: searchBy === 'Air Cargo' ? '#ffaf00' : '#fff',
                                            color: searchBy === 'Air Cargo' ? '#fff' : '#57534e',
                                            fontSize: 13, fontWeight: 600,
                                            cursor: airLocked ? (searchBy === 'Air Cargo' ? 'default' : 'not-allowed') : 'pointer',
                                            opacity: airLocked && searchBy !== 'Air Cargo' ? 0.5 : 1,
                                            boxShadow: searchBy === 'Air Cargo' ? '0 2px 10px rgba(255,175,0,.30)' : 'none',
                                            transition: 'all .15s',
                                        }}
                                    >
                                        <img
                                            src={searchBy === 'Air Cargo' ? activeflighticon : flighticon}
                                            alt="Air"
                                            style={{ width: 20, height: 20, filter: searchBy === 'Air Cargo' ? 'brightness(0) invert(1)' : 'none' }}
                                        />
                                        Air Cargo
                                    </button>

                                    {/* Ocean Cargo */}
                                    <button
                                        type="button"
                                        disabled={oceanLocked}
                                        onClick={() => { setSearchBy('Ocean Cargo'); if (Object.keys(allSarchState?.data).length > 0) { dispatch(clearstate()) } }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            height: 36, padding: '0 15px',
                                            borderRadius: 10,
                                            border: `2px solid ${searchBy === 'Ocean Cargo' ? '#ffaf00' : '#e7e0d6'}`,
                                            background: searchBy === 'Ocean Cargo' ? '#ffaf00' : '#fff',
                                            color: searchBy === 'Ocean Cargo' ? '#fff' : '#57534e',
                                            fontSize: 13, fontWeight: 600,
                                            // When Ocean is the locked-in mode it stays highlighted
                                            // and just non-interactive (no dimming); when it's a
                                            // disabled non-active mode (e.g. editing an Air query)
                                            // it dims like the other locked buttons.
                                            cursor: oceanLocked ? (searchBy === 'Ocean Cargo' ? 'default' : 'not-allowed') : 'pointer',
                                            opacity: oceanLocked && searchBy !== 'Ocean Cargo' ? 0.5 : 1,
                                            boxShadow: searchBy === 'Ocean Cargo' ? '0 2px 10px rgba(255,175,0,.30)' : 'none',
                                            transition: 'all .15s',
                                        }}
                                    >
                                        <img
                                            src={searchBy === 'Ocean Cargo' ? activeshipicon : shipicon}
                                            alt="Ocean"
                                            style={{ width: 20, height: 20, filter: searchBy === 'Ocean Cargo' ? 'brightness(0) invert(1)' : 'none' }}
                                        />
                                        Ocean Cargo
                                    </button>

                                    {/* Courier */}
                                    <button
                                        type="button"
                                        disabled={courierLocked}
                                        onClick={() => handleSignOn()}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            height: 36, padding: '0 15px',
                                            borderRadius: 10,
                                            border: '2px solid #e7e0d6',
                                            background: '#fafaf9',
                                            color: '#57534e',
                                            fontSize: 13, fontWeight: 600,
                                            cursor: courierLocked ? 'not-allowed' : 'pointer',
                                            opacity: courierLocked ? 0.5 : 1,
                                            transition: 'all .15s',
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#a8a29e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                                            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                                            <line x1="12" y1="12" x2="12" y2="12" />
                                        </svg>
                                        Courier
                                        {/* <span style={{
                                            fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
                                            background: '#f0e8d5', color: '#b45309',
                                            borderRadius: 4, padding: '2px 5px',
                                            textTransform: 'uppercase',
                                        }}>Soon</span> */}
                                    </button>
                                </div>
                            </div>

                            {/* ── Form content ────────────────────────────────────────── */}
                            {
                                bookingPrefillLoading
                                    ? ''
                                    : searchBy === 'Ocean Cargo'
                                        ? <ShipmentRates
                                        searchBy={searchBy}
                                        searchFormData={allSarchState?.data?.data?.nodeData}
                                        // A KAM only ever reaches this page via "Edit Query"
                                        // (deep-linking back into an already-established
                                        // customer query, see bookingQueryId above) — never
                                        // an independent search. Same lock-down as Queries.jsx's
                                        // inline ShipmentRateForm: only Cutoff Date stays editable.
                                        type={userroles === "kam" ? "kam_admin_edit" : undefined}
                                      />
                                        : searchBy === 'Air Cargo'
                                            ? <NewsearchForm searchBy={searchBy} searchFormData={allSarchState?.searchratesAvailableData?.nodeData} />
                                            : ''
                            }
                        </div>
                    </div>
                </Spin>
            }
            {contextHolder}
        </>
    )
}
export default NewSearchRates